<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{ 'change-date-popup-overlay': true, 'is-visible': isVisible }">
    <div :class="{ 'change-date-popup': true, 'is-visible': isVisible }">
      <a
        href="#"
        class="change-date-popup-close"
        @click.prevent="close()">
        &times;
      </a>

      <h2 v-pure-html="popupTitle"></h2>

      <a
        href="#"
        v-if="!usesCustomHour"
        class="non-standard-date"
        slot="header-content"
        @click.prevent="setCustomHourState(true)">
        <icon
          name="edit"
          primary-color="color-notice"
          size="xxs"></icon>
        {{ $t('reservationForm.nonStandardTerm') }}
      </a>

      <a
        href="#"
        v-if="usesCustomHour"
        class="use-standard-date"
        slot="header-content"
        @click.prevent="setCustomHourState(false)">
        <icon
          name="cross-circle"
          primary-color="color-danger"
          size="xxs"></icon>
        {{ $t('reservationForm.useAvailableTerm') }}
      </a>

      <reservation-form-custom-hour
        v-if="usesCustomHour"
        :useDaysMode="useDaysMode"
        :changeDatePopupMode="true"
        :selectedWorker="originalWorker"
        :checkTermMode="mode"
        :reservationData="reservationData"
        slot="content" />

      <template v-if="!usesCustomHour">
        <field v-if="useDaysMode">
          <label slot="label">
            <template v-if="!dailyEndAvailable">{{ $t('common.ui.daysNumber') }}:</template>
            <template v-else>{{ $t('common.ui.nightsNumber') }}:</template>
          </label>
          <input
            slot="field"
            type="number"
            min="1"
            :class="{ 'is-invalid': errorDaysNumber }"
            @input="errorDaysNumber = false"
            v-model="selectedDate.days" />
        </field>

        <div class="reservation-date-switcher">
          <a
            href="#"
            class="reservation-date-switcher-prev"
            @click.prevent="switchDate(useDaysMode ? 'month' : 'day', -1)">
          </a>

          <small class="reservation-date-switcher-dayname">
            <template v-if="!useDaysMode">{{ dateSwitcherDayName }}</template>
            <template v-else>&nbsp;</template>
          </small>

          <strong class="reservation-date-switcher-fulldate">
            {{ dateSwitcherFullDate }}
            <icon
              name="edit-circle"
              class="edit-date"
              properties="is-clickable"
              primaryColor="color-notice"
              size="xs"
              @click.native="editSelectedDate()" />
          </strong>

          <date-picker
            :language="$t('common.ui.datepickerLanguage')"
            :full-month-name="true"
            :value="datePickerDate"
            ref="date-picker"
            @selected="changeSelectedDate"
            :format="$t('common.ui.datepickerFormat')"
            :monday-first="true"
            :minimum-view="useDaysMode ? 'month' : 'day'"></date-picker>

          <a
            href="#"
            class="reservation-date-switcher-next"
            @click.prevent="switchDate(useDaysMode ? 'month' : 'day', 1)">
          </a>
        </div>

        <div
          v-if="nearestDatesAreLoading"
          class="reservation-loading-state"
          slot="content">
          {{ $t('changeDatePopup.loadingTerms') }}
        </div>

        <div
          v-if="!nearestDatesAreLoading && nearestDatesMessage"
          class="reservation-error-message"
          slot="content">
          {{ nearestDatesMessage }}
        </div>

        <div
          v-if="showDatesWrapper"
          class="reservation-nearest-dates-wrapper"
          slot="content">
          <div
            v-if="nearestDates"
            class="reservation-nearest-dates">
            <div
              v-for="(dayData, index) of nearestDates"
              :key="'nearest-date-' + index"
              class="reservation-nearest-dates-day">
              <template v-if="useDaysMode">
                <div
                  v-if="!dayItemIsActive(dayData.date_from)"
                  :key="'nearest-day-' + index"
                  :class="{ 'reservation-nearest-dates-day-item': true, 'is-invalid': !dayData.details.available }">
                  <day :day="dayData" />

                  <i
                    v-if="!dayData.details.available"
                    class="item-info"
                    @click.stop.prevent="showInfo('item-info-days-' + dayData.date_from + '-' + dayData.date_to)">
                    !
                    <info-box
                      :ref="'item-info-days-' + dayData.date_from + '-' + dayData.date_to"
                      :spacing="32"
                      position="right"
                      parentSelectorWithOverflow=".reservation-nearest-dates">
                      <div slot="content" v-pure-html="dayData.details.message"></div>
                    </info-box>
                  </i>

                  <btn
                    type="small centered blue"
                    :disabled="!dayData.details.available"
                    @click.native="selectReservationDate(dayData.date_from, false, 'nearest-date-' + index)">
                    {{ $t('reservationForm.select') }}
                  </btn>
                </div>

                <div
                  v-if="dayItemIsActive(dayData.date_from)"
                  :key="'nearest-day-' + index"
                  :class="{ 'reservation-nearest-dates-day-item': true, 'is-active': true }"
                  @click="clearReservationDate()">
                  <day :day="dayData" />

                  <icon
                    name="ok"
                    size="xs"
                    class="active-icon"
                    primaryColor="color-notice" />
                </div>
              </template>

              <template v-else>
                <div
                  v-if="dayData && !hourItemIsActive(dayData.hour)"
                  :class="{ 'reservation-nearest-dates-day-item': true, 'is-invalid': !dayData.valid }">
                  <hour
                    :value="dayData.hour"
                    :hasBorder="false"
                    :hasPadding="true" />

                  <i
                    v-if="!dayData.valid"
                    class="item-info"
                    @click.stop.prevent="showInfo('item-info-1-' + dayData.hour + '-' + dayData.date)">
                    !
                    <info-box
                      :ref="'item-info-1-' + dayData.hour + '-' + dayData.date"
                      :spacing="32"
                      position="right"
                      parentSelectorWithOverflow=".reservation-nearest-dates">
                      <div slot="content" v-pure-html="dayData.message"></div>
                    </info-box>
                  </i>

                  <label v-if="workers && workers.length">
                    {{ $t('common.ui.worker') }}
                    <select
                      v-if="dayData.free_workers && dayData.free_workers.length"
                      :ref="'nearest-date-' + index">
                      <option
                        v-for="workerData of dayData.free_workers"
                        :key="'worker-nearest-date-2-' + index + '-' + workerData.id"
                        :value="workerData.id">
                        {{ workerData.name }}
                      </option>
                    </select>

                    <select
                      v-if="(dayData.free_workers && !dayData.free_workers.length) && workers && workers.length"
                      :ref="'nearest-date-' + index">
                      <option
                        v-for="workerData of filteredWorkers"
                        :key="'worker-nearest-date-3-' + index + '-' + workerData.id"
                        :value="workerData.id">
                        {{ workerData.name }}
                      </option>
                    </select>
                  </label>

                  <btn
                    type="small blue"
                    @click.native="selectReservationDate(selectedDate.date, dayData.hour, 'nearest-date-' + index)">
                    {{ $t('changeDatePopup.select') }}
                  </btn>
                </div>

                <div
                  v-if="dayData && hourItemIsActive(dayData.hour)"
                  :key="'nearest-date-' + index"
                  :class="{ 'reservation-nearest-dates-day-item': true, 'is-invalid': !dayData.valid, 'is-active': true }"
                  @click="clearReservationDate()">
                  <hour
                    :value="dayData.hour"
                    :hasBorder="false"
                    :hasPadding="true" />

                  <i
                    v-if="!dayData.valid"
                    class="item-info"
                    @click.stop.prevent="showInfo('item-info-2-' + dayData.hour + '-' + dayData.date)">
                    !
                    <info-box
                      :ref="'item-info-2-' + dayData.hour + '-' + dayData.date"
                      :spacing="32"
                      position="right"
                      parentSelectorWithOverflow=".reservation-nearest-dates">
                      <div slot="content" v-pure-html="dayData.message"></div>
                    </info-box>
                  </i>

                  <label v-if="selectedWorkerName()">
                    {{ $t('common.ui.worker') }}
                    <strong>
                      {{ selectedWorkerName() }}
                    </strong>
                  </label>

                  <icon
                    name="ok"
                    size="xs"
                    class="active-icon"
                    primaryColor="color-notice" />
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>

      <btn
        type="popup-left primary"
        @click.native="askForNotify()"
        :disabled="saveInProgress || (!useDaysMode && (selectedDate.hour === '' || (workersEnabled && selectedDate.worker === '')))">
        <template v-if="mode === 'edit-date'">{{ $t('changeDatePopup.changeTerm') }}</template>
        <template v-else>{{ $t('changeDatePopup.copyReservation') }}</template>
      </btn>

      <btn
        type="popup-right"
        @click.native="close">
        {{ $t('common.ui.close') }}
      </btn>
    </div>
  </div>
</template>

<script>
import Day from './../common/Day';
import debounce from 'lodash.debounce';
import Configuration from './../mixins/Configuration';
import CalendarUtils from './../helpers/CalendarUtils';
import DatePicker from 'vuejs-datepicker';
import ErrorUtils from './../helpers/ErrorUtils.js';
import Hour from './../common/Hour';
import PopupZindex from './../mixins/PopupZindex';
import ReservationFormCustomHour from './../ui/ReservationFormCustomHour';
import Tooltip from './../common/Tooltip';
import UserTypes from './../mixins/UserTypes';
import WordVariations from './../mixins/WordVariations';

export default {
  name: 'change-date-popup',
  mixins: [
    Configuration,
    PopupZindex,
    UserTypes,
    WordVariations
  ],
  components: {
    'date-picker': DatePicker,
    'day': Day,
    'hour': Hour,
    'reservation-form-custom-hour': ReservationFormCustomHour,
    'tooltip': Tooltip
  },
  data () {
    return {
      isVisible: false,
      mode: 'edit-date',
      reservationData: {},
      selectedDate: {
        date: '',
        month: '',
        hour: '',
        worker: '',
        days: null
      },
      originalWorker: null,
      datePickerDate: null,
      nearestDates: false,
      nearestDatesMessage: '',
      nearestDatesAreLoading: true,
      workersLoaded: false,
      usesCustomHour: false,
      workers: [],
      errorDaysNumber: false,
      saveInProgress: false
    };
  },
  computed: {
    useDaysMode () {
      return this.reservationData.type === 1;
    },
    popupTitle () {
      if (this.mode === 'edit-date') {
        return this.$t('changeDatePopup.changeReservationDate');
      }

      return this.$t('changeDatePopup.copyReservationTitle');
    },
    showDatesWrapper () {
      return (
        !this.nearestDatesAreLoading &&
        !this.nearestDatesMessage &&
        this.nearestDates.length
      );
    },
    dateSwitcherDayName () {
      let date = this.selectedDate.date.split('-');
      let dateObject = new Date(date[0], date[1] - 1, date[2], 0, 0, 0, 0);

      return CalendarUtils.getDayName(dateObject.getDay());
    },
    dateSwitcherFullDate () {
      return this.humanDateFormat(this.selectedDate.date, false);
    },
    filteredWorkers () {
      if (this.userIsWorker && this.userCanManageOwn) {
        return this.workers.filter(worker => worker.id === this.$store.state.config.user.id);
      }

      if (this.servicesEnabled && this.reservationData.service) {
        let workers = this.workers.filter(item => {
          return item.worker.services.filter(service => service.id === this.reservationData.service.id).length;
        });

        if (this.reservationData.worker) {
          workers = workers.filter(worker => worker.id === this.reservationData.worker.id);
        }

        return workers;
      }

      if (this.reservationData.worker) {
        return this.workers.filter(worker => worker.id === this.reservationData.worker.id);
      }

      return this.workers;
    }
  },
  mounted () {
    this.$bus.$on('show-change-date-popup', this.show);
    this.$bus.$on('hide-change-date-popup', this.close);
  },
  watch: {
    'selectedDate.date': function (newValue, oldValue) {
      if (!this.useDaysMode && !this.usesCustomHour && newValue !== '' && oldValue !== '') {
        this.selectedDate.hour = '';
        this.selectedDate.worker = '';
        this.loadData();
      }
    },
    'selectedDate.month': function (newValue, oldValue) {
      if (oldValue !== '') {
        this.loadData();
      }
    },
    'selectedDate.days': function (newValue, oldValue) {
      if (oldValue !== null && newValue && newValue > 0) {
        this.loadData();
      }
    },
    'usesCustomHour': function (newValue, oldValue) {
      if (oldValue === true && newValue === false) {
        this.selectedDate.hour = '';
        this.selectedDate.worker = '';
        this.loadData();
      }
    }
  },
  methods: {
    show (mode, reservationData, dailyEndAvailable) {
      this.mode = mode;
      this.reservationData = reservationData;
      this.isVisible = true;
      this.selectedDate.date = this.reservationData.date;
      this.selectedDate.month = this.reservationData.month;
      this.selectedDate.hour = this.reservationData.time;
      this.dailyEndAvailable = dailyEndAvailable;
      this.usesCustomHour = false;

      if (this.useDaysMode) {
        this.selectedDate.days = this.reservationData.days;
      }

      if (this.workersEnabled && this.reservationData.worker) {
        this.selectedDate.worker = this.reservationData.worker.id;
        this.originalWorker = this.reservationData.worker.id;
      }

      this.datePickerDate = this.reservationData.date;

      if (this.workersEnabled && !this.useDaysMode) {
        this.loadWorkers();
      } else {
        this.loadData();
      }

      this.increasePopupZindex();
    },
    loadData: debounce(function () {
      this.nearestDatesAreLoading = true;
      this.loadNearestDates();
    }, 500),
    loadNearestDates () {
      let config = {};

      if (this.mode !== 'copy-reservation') {
        config.inquiry_id = parseInt(this.reservationData.id, 10);
      }

      if (this.reservationData.confirm_email !== '') {
        config.email = this.reservationData.confirm_email;
      }

      if (this.reservationData.phone !== '') {
        config.phone = this.reservationData.phone;
      }

      if (this.reservationData.people_number !== '') {
        config.people = this.reservationData.people_number;
      }

      if (this.servicesEnabled && this.reservationData.service) {
        config.service = this.reservationData.service.id;
      }

      if (this.workersEnabled && this.reservationData.worker) {
        config.worker = this.reservationData.worker.id;
      }

      config.date = this.selectedDate.date;
      config.plugin_comment = this.reservationData.plugin_comment;

      let endpoint = '/panel/reservations/get-date-hours';

      if (this.useDaysMode) {
        endpoint = '/panel/reservations/get-date-days';
        config.days = this.selectedDate.days;

        if (this.reservationData.children) {
          config.children = this.reservationData.children;
        } else {
          config.children = 0;
        }
      } else {
        config.duration = this.reservationData.duration;
      }

      this.$http.post(endpoint, config).then(response => {
        this.nearestDatesMessage = response.data.message;

        if (this.useDaysMode) {
          let tempData = response.data.data;
          let dates = Object.keys(tempData);
          this.nearestDates = [];

          for (let i = 0; i < dates.length; i++) {
            this.nearestDates.push(tempData[dates[i]]);
          }
        } else {
          this.nearestDates = response.data.data;
        }

        this.nearestDatesAreLoading = false;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, endpoint, this.$router, this.$bus, this.loadNearestDates.bind(this));
        this.nearestDatesAreLoading = false;
      });
    },
    loadWorkers () {
      this.$http.post('/panel/workers/get-workers').then(response => {
        this.workers = response.data.users.filter(worker => worker.worker_type === 0);
        this.workersLoaded = true;
        this.loadData();
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/workers/get-workers', this.$router, this.$bus, this.loadWorkers.bind(this));
      });
    },
    close () {
      this.isVisible = false;
      this.reservationData = false;
      this.nearestDates = false;
      this.nearestDatesMessage = '';
      this.nearestDatesAreLoading = true;
      this.workersLoaded = false;
      this.dailyEndAvailable = false;
      this.workers = [];
      this.removePopupZindex();
    },
    switchDate (type, offset) {
      if (type === 'day') {
        let date = this.selectedDate.date.split('-');
        let dateObject = new Date(date[0], date[1] - 1, date[2], 0, 0, 0, 0);
        dateObject.setDate(dateObject.getDate() + offset);
        this.selectedDate.date = [dateObject.getFullYear(), dateObject.getMonth() + 1, dateObject.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
        this.datePickerDate = this.selectedDate.date;
        this.loadData();
      } else if (type === 'month') {
        let date = this.selectedDate.date.split('-');
        let dateObject = new Date(date[0], date[1] - 1, date[2], 0, 0, 0, 0);
        dateObject.setMonth(dateObject.getMonth() + offset);
        this.selectedDate.date = [dateObject.getFullYear(), dateObject.getMonth() + 1, dateObject.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
        this.datePickerDate = this.selectedDate.date;
        this.loadData();
      }
    },
    editSelectedDate () {
      this.$refs['date-picker'].showCalendar();
    },
    selectReservationDate (date, hour, workerSelectID) {
      let workerID = '';

      if (this.workersEnabled && !this.useDaysMode) {
        workerID = this.$refs[workerSelectID][0].value;
      }

      this.selectedDate.date = date;

      if (!this.useDaysMode) {
        this.selectedDate.hour = hour;
        this.selectedDate.worker = workerID;
      }
    },
    clearReservationDate () {
      this.selectedDate.date = '';
      this.selectedDate.hour = '';
      this.selectedDate.worker = '';
    },
    hourItemIsActive (hour) {
      return hour === this.selectedDate.hour;
    },
    dayItemIsActive (date) {
      return date === this.selectedDate.date;
    },
    selectedWorkerName () {
      let worker = this.workers.filter(worker => worker.id === parseInt(this.selectedDate.worker, 10));

      if (worker.length) {
        return worker[0].name;
      }

      return '';
    },
    showInfo (id) {
      this.$bus.$emit('body-clicked');

      setTimeout(() => {
        this.$refs[id][0].show();
      }, 0);
    },
    humanDateFormat (date, withHour = true, showDayName = false) {
      if (!date) {
        return '';
      }

      if (this.useDaysMode) {
        return CalendarUtils.humanDateFormatMonth(date);
      }

      return CalendarUtils.humanDateFormat(date, withHour, showDayName);
    },
    changeSelectedDate (newValue) {
      let month = newValue.getMonth() + 1.0;
      let day = newValue.getDate();

      if (month < 10) {
        month = '0' + month;
      }

      if (day < 10) {
        day = '0' + day;
      }

      if (this.useDaysMode) {
        this.selectedDate.month = [newValue.getFullYear(), month, day].join('-');
      } else {
        this.selectedDate.date = [newValue.getFullYear(), month, day].join('-');
      }
    },
    askForNotify () {
      if (this.useDaysMode && (!this.selectedDate.days || this.selectedDate.days < 0)) {
        this.errorDaysNumber = true;
        return;
      }

      this.$bus.$emit('confirm-display', {
        title: this.$t('changeDatePopup.clientNotificationTitle'),
        desc: this.$t('changeDatePopup.clientNotificationDesc'),
        cancelLabel: this.$t('changeDatePopup.clientNotificationBtnCancel'),
        cancelClick: () => this.saveChanges(false),
        okLabel: this.$t('changeDatePopup.clientNotificationBtnOk'),
        okClick: () => this.saveChanges(true),
        isDanger: false
      });
    },
    saveChanges (notifyUser = false) {
      this.saveInProgress = true;
      let endpoint = '/panel/reservations/copy';

      if (this.mode === 'edit-date') {
        endpoint = '/panel/reservations/change-date';
      }

      let config = {
        id: this.reservationData.id,
        date: this.selectedDate.date,
        notify: +notifyUser
      };

      if (this.useDaysMode) {
        config.days = this.selectedDate.days;
      } else {
        config.time = this.selectedDate.hour;
      }

      if (!this.useDaysMode && this.workersEnabled) {
        config.worker_id = this.selectedDate.worker;
      }

      this.$http.post(endpoint, config).then(response => {
        this.$bus.$emit('calendar-layout-reload-needed');
        this.$bus.$emit('reservation-details-popup-reload-needed');
        this.$bus.$emit('customer-reload-needed');
        this.$bus.$emit('dashboard-reservations-list-reload-needed');
        this.$bus.$emit('calendar-day-popup-reload-reservations');

        let messageText = this.$t('changeDatePopup.reservationTermChanged');

        if (this.mode !== 'edit-date') {
          messageText = this.$t('changeDatePopup.reservationSucessfullyCopied');
        }

        if (response.data.response === 1) {
          this.close();
          this.$bus.$emit('notification-display', {
            type: 'success',
            message: messageText
          });
        } else {
          this.$bus.$emit('notification-display', {
            type: 'error',
            message: response.data.message
          });
        }

        this.saveInProgress = false;
      }).catch(error => {
        this.saveInProgress = false;
        ErrorUtils.handleSubmitError(error, endpoint, this.$router, this.$bus, this.saveChanges.bind(this, notifyUser));
      });
    },
    setCustomHourState (isUsed) {
      this.usesCustomHour = isUsed;
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-change-date-popup', this.show);
    this.$bus.$off('hide-change-date-popup', this.close);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.change-date-popup {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, .125);
  display: none;
  left: 50%;
  min-height: 640px;
  padding: 3rem 4rem 8rem 4rem;
  position: fixed;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 720px;
  z-index: 10000;

  &-overlay {
    background: rgba(0, 0, 0, .25);
    height: 100%;
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: fixed;
    top: 0;
    transition: opacity .25s ease-out;
    width: 100vw;
    will-change: opacity;
    z-index: 10000;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;

      .confirm {
        transform: translateX(-50%) translateY(-50%);
      }
    }
  }

  &.is-visible {
    display: block;
  }

  .non-standard-date,
  .use-standard-date {
    position: absolute;
    right: 30px;
    top: 35px;
  }

  .reservation-form-custom-hour {
    margin-top: 20px;
  }

  .change-date-popup-empty-state {
    color: $color-text-light;
    font-size: 2.4rem;
    padding: 4.8rem 0 3.2rem 0;
    text-align: center;
    width: 100%;
  }

  & > h2 {
    align-items: flex-end;
    display: flex;
    margin: 0;

    & > small {
      color: $color-text-light;
      margin-left: 1rem;
    }
  }

  .button-popup-left {
    border-radius: 0 0 0 $border-radius-small;
  }

  .button-popup-right {
    border-radius: 0 0 $border-radius-small 0;
  }

  &-close {
    background: $color-danger;
    border-radius: 50%;
    color: $color-light;
    font-size: 3.6rem;
    font-weight: bold;
    height: 3.6rem;
    line-height: 3.5rem;
    position: absolute;
    right: -1.8rem;
    text-align: center;
    top: -1.8rem;
    width: 3.6rem;
  }

  .reservation-date-switcher {
    margin: 3rem auto -2rem auto;
    position: relative;
    text-align: center;
    user-select: none;
    width: 100%;

    &-dayname {
      color: $color-text-light;
      display: block;
      font-size: 1.6rem;
      margin-bottom: .5rem;
    }

    &-fulldate,
    &-fulltime {
      display: block;
      font-size: 2.4rem;
      font-weight: 500;
    }

    &-prev,
    &-next {
      height: 26px;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 20px;

      &:before,
      &:after {
        background: $color-bg-dark;
        content: "";
        display: block;
        height: 2px;
        position: absolute;
        top: 50%;
        transform-origin: 0 50%;
        width: 14px;
      }

      &:active,
      &:focus,
      &:hover {
        opacity: .5;
      }
    }

    &-prev {
      left: 0;

      &:before,
      &:after {
        left: 0;
      }

      &:before {
        transform: rotate(-45deg);
      }

      &:after {
        transform: rotate(45deg);
      }

      &.is-inactive {
        opacity: .25;
        pointer-events: none;
      }
    }

    &-next {
      right: 0;

      &:before,
      &:after {
        transform-origin: 100% 50%;
        right: 0;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }
  }

  .field + .reservation-date-switcher {
    margin: 0 auto -2rem auto;
  }

  .reservation-nearest-dates {
    margin: 1.5rem auto;
    max-height: 360px;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0 2rem 0 0;
    width: 600px;

    &-wrapper {
      background: $color-bg-gray-light;
      border: 1px solid $color-border-light;
      margin: 3rem auto 0 auto;
      max-width: 840px;
      width: 100%;
    }

    .nearest-dates-notice {
      color: $color-text-light;
      font-size: 1.3rem;
      margin-bottom: 1.5rem;
      text-align: center;
    }

    h3 {
      font-size: 2rem;
      font-weight: 500;
      margin: 2rem 0;
    }

    &-day {
      &-item {
        align-items: center;
        background: $color-bg-lightest;
        border: 1px solid $color-border;
        border-radius: $border-radius-small;
        display: flex;
        margin: .5rem 0;
        padding: .5rem 1rem;

        &.is-active {
          background: linear-gradient($color-button-active-top, $color-button-active-bottom);
          border: 2px solid $color-notice;
          box-shadow: 0 2px 5px rgba(0, 0, 0, .25);
        }

        &.is-invalid {
          background: linear-gradient($color-light, lighten($color-button-danger-top, 33%));
          border: 1px solid $color-danger;

          .button {
            background: $color-button-danger-top;
            border-color: $color-button-danger-top;

            &:active,
            &:focus,
            &:hover {
              background: $color-button-danger-bottom;
              border-color: $color-button-danger-bottom;
            }
          }

          .item-info {
            align-items: center;
            background: $color-button-danger-top;
            border-radius: 10px;
            color: $color-light;
            cursor: pointer;
            display: flex;
            font-family: serif;
            font-size: 17px;
            font-style: normal;
            font-weight: bold;
            justify-content: center;
            height: 20px;
            position: relative;
            top: 1px;
            width: 20px;
          }

          &.is-active {
            background: linear-gradient($color-button-active-top, $color-button-active-bottom);
            border: 2px solid $color-notice;
            box-shadow: 0 2px 5px rgba(0, 0, 0, .25);
          }
        }

        .nearest-day-date {
          font-weight: 400;
          width: 160px;
        }

        label {
          align-items: center;
          color: $color-text-light;
          display: flex;
          font-size: 1.2rem;
          margin-left: 3rem;

          select {
            font-size: 1.2rem;
            height: 2.6rem;
            margin-left: .5rem;
            width: 150px;
          }

          strong {
            margin-left: .5rem;
            width: 150px;
          }
        }

        .item-info + label {
          margin-left: 3rem;
        }

        .button {
          margin-left: auto;
          min-width: 80px;
        }

        select + .button {
          margin-left: 1rem;
        }

        .active-icon {
          margin: 0 32px 0 auto;
        }

        select + .active-icon {
          margin: 0 32px 0 44px;
        }
      }
    }
  }

  .reservation-empty-state,
  .reservation-loading-state,
  .reservation-error-message {
    color: $color-text-light;
    padding: 6rem 0 3rem 0;
    text-align: center;
    width: 100%;
  }

  .reservation-error-message {
    color: $color-danger;
    padding-left: 2rem;
  }

  .info-box-content > div {
    color: $color-text!important;
    font-family: $font-basic;
    font-size: 1.3rem;
  }
}

@media (max-width: 800px) {
  .change-date-popup {
    border-radius: 0;
    height: 100%;
    left: 0;
    padding: 2rem 2rem 8rem 2rem;
    top: 0;
    transform: none;
    width: 100vw;

    .change-date-popup-close {
      display: none;
    }

    .non-standard-date,
    .use-standard-date {
      position: absolute;
      right: 30px;
      top: 25px;
    }

    .button-popup-left,
    .button-popup-right {
      border-radius: 0;
    }

    .reservation-date-switcher {
      margin-top: 1rem;
    }

    .reservation-nearest-dates-wrapper {
      height: calc(100% - 10rem);
      margin-top: 1rem;

      .reservation-nearest-dates {
        height: 100%;
        margin: 0 auto;
        max-height: none;
        max-width: 96%;
        padding: 0;
      }
    }
  }
}

@media (max-width: 650px) {
  .change-date-popup {
    .non-standard-date,
    .use-standard-date {
      position: absolute;
      right: 50%;
      top: 54px;
      transform: translateX(50%);
    }

    .reservation-date-switcher,
    .reservation-form-custom-hour {
      margin-top: 4rem;
    }
  }
}

@media (max-width: 600px) {
  .change-date-popup {
    .reservation-nearest-dates-day-item {
      flex-wrap: wrap;

      .button {
        margin: 1rem 0 .5rem 0;
        width: 100%;
      }

      label {
        margin-left: auto;
      }
    }
  }
}

@media (max-width: 480px) {
  .change-date-popup {
    h2 {
      font-size: 20px;
    }

    .reservation-nearest-dates-day-item {
      position: relative;

      label {
        align-items: center;
        display: flex;
        height: 2.6rem;
        margin-left: 0!important;
        margin-top: 1rem;
        width: 100%;

        select {
          width: 100%;
        }
      }

      .hour-value {
        padding-left: 0;
        text-align: left;
      }

      svg {
        margin: 0!important;
        position: absolute;
        right: 1rem;
        top: 1rem;
      }

      .item-info {
        position: relative;
        top: 0!important;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/ChangeDatePopup.vue